import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
} from "@mui/material";
import services from "@piccadilly-cloud/platform-services";
import { piccIcons } from "components-piccadilly";
import { usePLRecordContext } from "contexts/plrecord/usePLRecordContext";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import FormProvider, { RHFTextField } from "../../../components/hook-form";
import Iconify from "../../../components/iconify";
import { useSessionContext } from "../../../contexts/session/useSessionContext";
import { PATH_AUTH } from "../../../routes/paths";
import GoogleAuth from "./partials/google-auth";
import { setErrorMessage } from "./utils";

export interface FormValuesProps {
  email: string;
  password: string;
  afterSubmit?: string;
}

interface LoginFormProps {
  onSuccess?: () => void;
}

export default function LoginForm({ onSuccess }: LoginFormProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSessionContext();
  const { login } = useSessionContext();
  const plrecord = usePLRecordContext();

  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      if (plrecord.initialized) {
        plrecord.reset();
      }
      await login(
        session.token,
        data.email,
        data.password,
        session.appConfig.appId
      );
      
      // Check if we're on OAuth callback page - don't navigate if so
      const isOAuthCallback = location.pathname.includes('/oauth') || 
                               location.pathname.includes('/auth/oauth');
      const hasOAuthParams = new URLSearchParams(location.search).has('response_type');

      if (onSuccess) {
        onSuccess();
      } else if (!isOAuthCallback && !hasOAuthParams) {
        // Only navigate if not on OAuth callback page
        const redirectTo =
          new URLSearchParams(window.location.search).get("redirect") ||
          new URLSearchParams(window.location.search).get("returnTo") ||
          "/";
        navigate(redirectTo);
      }
    } catch (error: any) {
      console.error(error);
      const message = {
        severity: "error",
        message: `login failed\n${data.email}\n${JSON.stringify(error)}`,
      };
      services.edge.analytics.oplog.create(message)({ token: session.token });

      setError("afterSubmit", {
        ...error,
        message: setErrorMessage(error),
      });
    }
  };

  const credentials = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!credentials) {
    console.error("Google client ID is not set");
  }

  const errorAlert = () => {
    if (!errors.afterSubmit || !errors.afterSubmit.message) {
      return null;
    }

    const message = errors.afterSubmit.message;

    if (message === "INACTIVE") {
      return (
        <Alert severity="error">
          This account is inactive. Please contact an admin.
        </Alert>
      );
    }

    return <Alert severity="error">{message}</Alert>;
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {errorAlert()}
        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={
                      showPassword
                        ? piccIcons.other.passwordView
                        : piccIcons.other.passwordHide
                    }
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link
          to={PATH_AUTH.forgotPassword}
          component={RouterLink}
          variant="body2"
          color="inherit"
          underline="always"
        >
          Forgot password?
        </Link>
      </Stack>

      <Box display="flex" flexDirection="column" gap={2}>
        <LoadingButton
          fullWidth
          color="primary"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitSuccessful || isSubmitting || isLoading}
        >
          Login
        </LoadingButton>
        {!!!session.appConfig.userSettings.disableTextMessageLogin && (
          <Button
            fullWidth
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => {
              navigate(PATH_AUTH.textMessage);
            }}
          >
            Text Message Login
          </Button>
        )}
        <GoogleAuth setError={setError} setLoading={setIsLoading} />
      </Box>
    </FormProvider>
  );
}
